/* eslint-disable import/no-unused-modules */
import { gql } from '@apollo/client';

export const GET_BLOGS = gql`
  query getHostingPlan {
    getHostingPlan {
      blogs {
        blogTitle
        defaultAddPosts
        id
        expiryDate
        hostingTrialExpiryDate
        plan
      }
    }
  }
`;

export const GET_HOSTING_PLAN = gql`
  query getHostingPlan {
    getHostingPlan {
      blogs {
        blogTitle
        blogSlogan
        logo
        darkLogo
        defaultDarkMode
        squareLogo
        logoDimensions {
          width
          height
        }
        darkLogoDimensions {
          width
          height
        }
        primaryColor
        defaultAddPosts
        redirectSubdomain
        googleAnalyticsId
        headerInjection
        useHostingUrl
        adstxt
        editKey
        id
        expiryDate
        hostingTrialExpiryDate
        isCancelled
        plan
        domain
        # deployedToday
        analyticsApi
        editorAvailable
        # Hosting v2 state: null (not offered), "available", "preview",
        # "requested" or "active". Requires a backend with hostingV2Status in
        # the schema — deploy order: tf-backend first.
        hostingV2Status
        hostingV2PreviewUrl
        hostingV2PreviewCode
        # A v2 blog's custom domain while it connects and once it serves.
        # Requires tf-backend with HostingV2Domain — deploy the backend first.
        hostingV2Domain {
          hostname
          alias
          aliasLive
          aliasPending
          step
          dnsTarget
          apex
          movingFromV1
          checkedAt
          records {
            type
            name
            host
            value
            live
          }
        }
      }
      hasVerifiedEmail
    }
  }
`;

// Stage 1 of the v1→v2 hosting switch: provisions a preview of this blog on
// the new hosting (the live blog keeps serving v1) — previewable through the
// editor session link once published.
export const PREPARE_HOSTING_V2 = gql`
  mutation prepareHostingV2($blogId: String!) {
    prepareHostingV2(blogId: $blogId) {
      success
      message
    }
  }
`;

// Stage 2: performs the flip server-side, instantly (ADR 105). Success with
// a still-'requested' status afterwards means the attempt failed and the
// card offers a retry.
export const REQUEST_HOSTING_V2_SWITCH = gql`
  mutation requestHostingV2Switch($blogId: String!) {
    requestHostingV2Switch(blogId: $blogId) {
      success
      message
    }
  }
`;

// v2 site editor (site-engine): mints a single-use entry URL server-side and
// redirects — same shape as stripeCustomerPortalLink. Only offered on blogs
// where getHostingPlan returns editorAvailable: true.
export const EDITOR_SESSION_LINK = gql`
  query editorSessionLink($blogId: String!) {
    editorSessionLink(blogId: $blogId)
  }
`;

export const CHECK_BLOG_AVAILABILITY = gql`
  query checkBlogAvailability($id: String!) {
    checkBlogAvailability(id: $id)
  }
`;

export const BLOG_DEPLOYMENT_STATE = gql`
  query blogDeploymentState($id: String!) {
    blogDeploymentState(id: $id)
  }
`;

export const CREATE_BLOG = gql`
  mutation createBlog(
    $id: String!
    $initialPosts: [String]
    $settings: BlogBasicSettings!
  ) {
    createBlog(id: $id, initialPosts: $initialPosts, settings: $settings) {
      success
      message
    }
  }
`;

export const BASIC_BLOG_SETTINGS = gql`
  mutation basicBlogSettings($id: String!, $settings: BlogBasicSettings!) {
    basicBlogSettings(id: $id, settings: $settings) {
      success
      message
    }
  }
`;

export const ADD_DOMAIN = gql`
  mutation addDomain($domain: String!, $blogId: String!) {
    addDomain(domain: $domain, blogId: $blogId) {
      success
      message
    }
  }
`;

// One finalize step for a v2 domain now (the backend loop also runs it).
export const CHECK_HOSTING_V2_DOMAIN = gql`
  mutation checkHostingV2Domain($blogId: String!) {
    checkHostingV2Domain(blogId: $blogId) {
      success
      message
    }
  }
`;

export const REMOVE_DOMAIN = gql`
  mutation removeDomain($blogId: String!) {
    removeDomain(blogId: $blogId) {
      success
      message
    }
  }
`;

export const CONFIRM_GUEST_COMMENT = gql`
  mutation confirmGuestComment($token: String!) {
    confirmGuestComment(token: $token) {
      success
      message
    }
  }
`;

// Attaches one published post's WordPress comments to it as guest comments.
// Sent a post at a time by the dashboard importer, and safe to re-send: the
// backend skips comments whose wpId it has already written.
export const IMPORT_GUEST_COMMENTS = gql`
  mutation importGuestComments(
    $blogId: String!
    $rootPermlink: String!
    $comments: [ImportedCommentInput!]!
  ) {
    importGuestComments(
      blogId: $blogId
      rootPermlink: $rootPermlink
      comments: $comments
    ) {
      success
      message
    }
  }
`;

export const UNGLAG_COMMENT = gql`
  mutation unFlagComment($commentId: String!) {
    unFlagComment(commentId: $commentId) {
      success
      message
    }
  }
`;
